// client/src/components/SiteInstructionModal.js
import React, { useState, useRef } from 'react';
import { siteInstructionAPI } from '../utils/api';

export default function SiteInstructionModal({ pack, existing, onClose, onIssued }) {
  // contractor_name and contractor_email are not present on list-row pack objects;
  // the consultant fills them in directly.
  const [name, setName] = useState(pack.contractor_name || '');
  const [email, setEmail] = useState(pack.contractor_email || '');
  const [reference, setReference] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const fileInputRef = useRef(null);

  const submit = async () => {
    setError('');
    if (!file) return setError('Please attach the cover letter PDF.');
    if (file.type !== 'application/pdf') return setError('The cover letter must be a PDF.');
    if (!name.trim()) return setError('Recipient name is required.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError('Enter a valid recipient email.');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('recipientName', name.trim());
    fd.append('recipientEmail', email.trim());
    if (pack.contractor_user_id) fd.append('recipientUserId', pack.contractor_user_id);
    if (reference.trim()) fd.append('reference', reference.trim());
    if (message.trim()) fd.append('message', message.trim());
    setBusy(true);
    try {
      const res = await siteInstructionAPI.issue(pack.id, fd);
      setDone(res.data);
      onIssued && onIssued(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to issue Site Instruction.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {existing ? 'Re-issue' : 'Issue'} Site Instruction — Station {pack.station_no}
        </h3>
        {existing && (
          <p className="text-xs text-amber-600 mb-3">
            Re-issuing supersedes the previous instruction and deactivates its link.
          </p>
        )}
        {done ? (
          <div className="text-sm text-emerald-700">
            Site Instruction issued to {done.recipient_name} and emailed to {done.recipient_email}.
            <div className="mt-4 text-right">
              <button className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-sm text-gray-700 mb-2">
              Recipient name
              <input
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Contractor full name"
              />
            </label>
            <label className="block text-sm text-gray-700 mb-2">
              Recipient email
              <input
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="contractor@example.com"
              />
            </label>
            <div className="flex gap-2">
              <label className="block text-sm text-gray-700 mb-2 flex-1">
                Reference (optional)
                <input
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={reference}
                  onChange={e => setReference(e.target.value)}
                  placeholder="TO6"
                />
              </label>
            </div>
            <label className="block text-sm text-gray-700 mb-2">
              Message (optional)
              <textarea
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </label>
            <label className="block text-sm text-gray-700 mb-2">
              Cover letter (PDF)
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="mt-1 w-full text-sm text-gray-700"
                onChange={e => setFile(e.target.files[0] || null)}
              />
            </label>
            <div className="bg-slate-50 rounded p-3 text-xs text-slate-600 my-3">
              <div className="font-medium mb-1">What will be sent (in order):</div>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Site Instruction cover letter (your upload)</li>
                <li>DRE approval letter</li>
                <li>Approved documents</li>
              </ol>
            </div>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                onClick={submit}
                disabled={busy}
              >
                {busy ? 'Merging & sending…' : 'Generate & Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
